import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Inbox, Eye, Calendar, Mail, Phone, Building2, 
  Palette, CheckCircle, XCircle, RefreshCw, Trash2,
  ChevronRight, ArrowLeft, MessageSquare, FileText,
  User, Clock, Settings, Lock, X, Image as ImageIcon,
  Users, Globe, Activity
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

// Local translation helper for component-level translations
const getTranslation = (content, language) => {
  if (content[language]) return content[language];
  if (content['en']) return content['en'];
  if (content['tr']) return content['tr'];
  return content[Object.keys(content)[0]];
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [templateRequests, setTemplateRequests] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newUsername: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const content = {
    en: {
      title: 'Admin Panel',
      subtitle: 'Manage requests and submissions',
      back: 'Back to Home',
      refresh: 'Refresh',
      tabs: {
        templates: 'Template Requests',
        contacts: 'Contact Messages',
        visitors: 'Visitors'
      },
      visitors: {
        title: 'Visitor Statistics',
        today: 'Today',
        todayUnique: 'Unique Today',
        total: 'Total',
        totalUnique: 'Total Unique',
        recent: 'Recent Visitors',
        page: 'Page',
        ip: 'IP',
        time: 'Time'
      },
      noData: 'No data yet',
      noDataDesc: 'When users submit forms, they will appear here.',
      status: 'Status',
      pending: 'Pending',
      new: 'New',
      reviewed: 'Reviewed',
      completed: 'Completed',
      brandName: 'Brand Name',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Message',
      description: 'Description',
      colors: 'Colors',
      createdAt: 'Submitted',
      viewDetails: 'View',
      close: 'Close',
      sendEmail: 'Send Email',
      total: 'Total',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this?',
      settings: 'Settings',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newUsername: 'New Username',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      save: 'Save Changes',
      passwordError: 'Current password is incorrect',
      passwordMismatch: 'Passwords do not match',
      passwordSuccess: 'Credentials updated successfully',
      logout: 'Logout'
    },
    tr: {
      title: 'Yönetim Paneli',
      subtitle: 'Talepleri ve mesajları yönetin',
      back: 'Ana Sayfaya Dön',
      refresh: 'Yenile',
      tabs: {
        templates: 'Şablon Talepleri',
        contacts: 'İletişim Mesajları',
        visitors: 'Ziyaretçiler'
      },
      visitors: {
        title: 'Ziyaretçi İstatistikleri',
        today: 'Bugün',
        todayUnique: 'Bugün Tekil',
        total: 'Toplam',
        totalUnique: 'Toplam Tekil',
        recent: 'Son Ziyaretçiler',
        page: 'Sayfa',
        ip: 'IP',
        time: 'Zaman'
      },
      noData: 'Henüz veri yok',
      noDataDesc: 'Kullanıcılar form gönderdiğinde burada görünecek.',
      status: 'Durum',
      pending: 'Bekliyor',
      new: 'Yeni',
      reviewed: 'İncelendi',
      completed: 'Tamamlandı',
      brandName: 'Marka Adı',
      name: 'İsim',
      email: 'E-posta',
      phone: 'Telefon',
      message: 'Mesaj',
      description: 'Açıklama',
      colors: 'Renkler',
      createdAt: 'Gönderilme',
      viewDetails: 'Görüntüle',
      close: 'Kapat',
      sendEmail: 'E-posta Gönder',
      total: 'Toplam',
      delete: 'Sil',
      confirmDelete: 'Bunu silmek istediğinizden emin misiniz?',
      settings: 'Ayarlar',
      changePassword: 'Şifre Değiştir',
      currentPassword: 'Mevcut Şifre',
      newUsername: 'Yeni Kullanıcı Adı',
      newPassword: 'Yeni Şifre',
      confirmPassword: 'Şifre Tekrar',
      save: 'Değişiklikleri Kaydet',
      passwordError: 'Mevcut şifre yanlış',
      passwordMismatch: 'Şifreler eşleşmiyor',
      passwordSuccess: 'Bilgiler başarıyla güncellendi',
      logout: 'Çıkış'
    }
  };
  const t = getTranslation(content, language);

  // Check admin auth
  useEffect(() => {
    const isAdmin = localStorage.getItem('adminAuth');
    if (!isAdmin) {
      navigate('/');
    }
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, contactsRes, visitorsRes, dashRes] = await Promise.all([
        fetch(`${API_URL}/api/template-requests`),
        fetch(`${API_URL}/api/contact-submissions`),
        fetch(`${API_URL}/api/admin/visitors`),
        fetch(`${API_URL}/api/admin/dashboard`)
      ]);
      
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplateRequests(data.requests || []);
      }
      
      if (contactsRes.ok) {
        const data = await contactsRes.json();
        setContactSubmissions(data.submissions || []);
      }
      
      if (visitorsRes.ok) {
        const data = await visitorsRes.json();
        setVisitorStats(data);
      }

      if (dashRes.ok) {
        const data = await dashRes.json();
        if (data.success) setDashboard(data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  };

  // Update submission status
  const updateStatus = async (kind, id, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/submissions/${kind}/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        if (kind === 'contact') {
          setContactSubmissions(prev => prev.map(s => s.submission_id === id ? { ...s, status: newStatus } : s));
        } else {
          setTemplateRequests(prev => prev.map(s => (s.id || s.request_id) === id ? { ...s, status: newStatus } : s));
        }
      }
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  // CSV export
  const exportCSV = (kind) => {
    window.open(`${API_URL}/api/admin/submissions/export.csv?kind=${kind}`, '_blank');
  };

  // Filter submissions (client-side, works with existing endpoints)
  const filteredSubmissions = React.useMemo(() => {
    let list = activeTab === 'templates' ? templateRequests : contactSubmissions;
    if (statusFilter !== 'all') {
      list = list.filter(s => (s.status || 'new') === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        (s.name || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q) ||
        (s.phone || '').toLowerCase().includes(q) ||
        (s.brandName || '').toLowerCase().includes(q) ||
        (s.company || '').toLowerCase().includes(q) ||
        (s.message || '').toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeTab, templateRequests, contactSubmissions, statusFilter, searchQuery]);

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'reviewed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'new': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return t.completed;
      case 'reviewed': return t.reviewed;
      case 'new': return t.new;
      default: return t.pending;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminUsername');
    localStorage.removeItem('adminPassword');
    navigate('/');
  };

  // Delete template request
  const handleDeleteTemplate = async (requestId) => {
    if (!window.confirm(t.confirmDelete)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/template-requests/${requestId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setTemplateRequests(prev => prev.filter(r => r.request_id !== requestId));
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // Delete contact submission
  const handleDeleteContact = async (submissionId) => {
    if (!window.confirm(t.confirmDelete)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/contact-submissions/${submissionId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setContactSubmissions(prev => prev.filter(c => c.submission_id !== submissionId));
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Get current stored credentials
    const storedUsername = localStorage.getItem('adminUsername') || 'pirone';
    const storedPassword = localStorage.getItem('adminPassword') || 'admin2026';

    // Verify current password
    if (passwordForm.currentPassword !== storedPassword) {
      setPasswordError(t.passwordError);
      return;
    }

    // Check if new passwords match
    if (passwordForm.newPassword && passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t.passwordMismatch);
      return;
    }

    // Update credentials
    if (passwordForm.newUsername) {
      localStorage.setItem('adminUsername', passwordForm.newUsername);
    }
    if (passwordForm.newPassword) {
      localStorage.setItem('adminPassword', passwordForm.newPassword);
    }

    setPasswordSuccess(t.passwordSuccess);
    setPasswordForm({ currentPassword: '', newUsername: '', newPassword: '', confirmPassword: '' });
    
    setTimeout(() => {
      setShowPasswordModal(false);
      setPasswordSuccess('');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-transparent pt-24 pb-12 relative z-10">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-white/40 hover:text-white text-sm mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              {t.back}
            </Link>
            <h1 className="text-3xl font-bold text-white">{t.title}</h1>
            <p className="text-white/50 mt-1">{t.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white/70 hover:bg-white/10 transition-colors"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              {t.refresh}
            </button>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white/70 hover:bg-white/10 transition-colors"
            >
              <Settings size={16} />
              {t.settings}
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors text-sm"
            >
              {t.logout}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#38BDF8]/10 flex items-center justify-center">
                <FileText size={18} className="text-[#38BDF8]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{templateRequests.length}</p>
                <p className="text-white/40 text-xs">{t.tabs.templates}</p>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <MessageSquare size={18} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{contactSubmissions.length}</p>
                <p className="text-white/40 text-xs">{t.tabs.contacts}</p>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Clock size={18} className="text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {templateRequests.filter(r => r.status === 'pending').length + contactSubmissions.filter(c => c.status === 'new').length}
                </p>
                <p className="text-white/40 text-xs">{t.pending}</p>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Inbox size={18} className="text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{templateRequests.length + contactSubmissions.length}</p>
                <p className="text-white/40 text-xs">{t.total}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'dashboard'
                ? 'bg-white text-black'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid="admin-tab-dashboard"
          >
            <Activity size={16} className="inline mr-2" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'templates' 
                ? 'bg-[#38BDF8] text-black' 
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid="admin-tab-templates"
          >
            <FileText size={16} className="inline mr-2" />
            {t.tabs.templates} ({templateRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'contacts' 
                ? 'bg-emerald-500 text-black' 
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid="admin-tab-contacts"
          >
            <MessageSquare size={16} className="inline mr-2" />
            {t.tabs.contacts} ({contactSubmissions.length})
          </button>
          <button
            onClick={() => setActiveTab('visitors')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'visitors' 
                ? 'bg-purple-500 text-white' 
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid="admin-tab-visitors"
          >
            <Users size={16} className="inline mr-2" />
            {t.tabs.visitors} ({visitorStats?.today || 0})
          </button>
        </div>

        {/* Search & Filter bar (only for template/contact tabs) */}
        {(activeTab === 'templates' || activeTab === 'contacts') && (
          <div className="flex flex-col md:flex-row gap-3 mb-6 items-stretch md:items-center">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'tr' ? 'İsim, e-posta, mesaj ara...' : 'Search name, email, message...'}
              className="flex-1 px-4 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-white/30"
              data-testid="admin-search-input"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-white focus:outline-none focus:border-white/30"
              data-testid="admin-status-filter"
            >
              <option value="all">{language === 'tr' ? 'Tümü' : 'All Status'}</option>
              <option value="new">{language === 'tr' ? 'Yeni' : 'New'}</option>
              <option value="contacted">{language === 'tr' ? 'İletişime Geçildi' : 'Contacted'}</option>
              <option value="won">{language === 'tr' ? 'Müşteri Oldu' : 'Won'}</option>
              <option value="lost">{language === 'tr' ? 'Kaybedildi' : 'Lost'}</option>
              <option value="pending">{language === 'tr' ? 'Beklemede' : 'Pending'}</option>
            </select>
            <button
              onClick={() => exportCSV(activeTab === 'templates' ? 'template' : 'contact')}
              className="px-4 py-2.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg font-medium hover:bg-emerald-500/20 transition-colors whitespace-nowrap"
              data-testid="admin-export-csv"
            >
              CSV Export
            </button>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && !loading && (
          <div className="space-y-6" data-testid="admin-dashboard">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: language === 'tr' ? 'Bugün İletişim' : 'Contacts Today', value: dashboard?.contacts?.today || 0, color: '#38BDF8' },
                { label: language === 'tr' ? 'Bu Hafta' : 'This Week', value: dashboard?.contacts?.week || 0, color: '#10B981' },
                { label: language === 'tr' ? 'Bu Ay' : 'This Month', value: dashboard?.contacts?.month || 0, color: '#F59E0B' },
                { label: language === 'tr' ? 'Toplam' : 'Total', value: dashboard?.contacts?.total || 0, color: '#EC4899' }
              ].map((kpi, i) => (
                <div key={i} className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
                  <div className="text-xs uppercase tracking-wider text-white/40 mb-2">{kpi.label}</div>
                  <div className="text-3xl font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
                </div>
              ))}
            </div>

            {/* Status Breakdown */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <MessageSquare size={16} className="text-emerald-400" />
                  {language === 'tr' ? 'İletişim Durumu' : 'Contact Status'}
                </h3>
                <div className="space-y-2">
                  {Object.entries(dashboard?.contacts?.byStatus || { new: 0 }).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between text-sm">
                      <span className="text-white/60 capitalize">{status}</span>
                      <span className="text-white font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <FileText size={16} className="text-[#38BDF8]" />
                  {language === 'tr' ? 'Şablon Talep Durumu' : 'Template Status'}
                </h3>
                <div className="space-y-2">
                  {Object.entries(dashboard?.templates?.byStatus || { pending: 0 }).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between text-sm">
                      <span className="text-white/60 capitalize">{status}</span>
                      <span className="text-white font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Visitor summary */}
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Users size={16} className="text-purple-400" />
                {language === 'tr' ? 'Ziyaretçi Özeti' : 'Visitor Summary'}
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <div className="text-2xl font-bold text-white">{dashboard?.visitors?.today || 0}</div>
                  <div className="text-xs text-white/40 uppercase">Today</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{dashboard?.visitors?.week || 0}</div>
                  <div className="text-xs text-white/40 uppercase">This Week</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{dashboard?.visitors?.total || 0}</div>
                  <div className="text-xs text-white/40 uppercase">Total</div>
                </div>
              </div>
              {dashboard?.visitors?.topPages?.length > 0 && (
                <div>
                  <div className="text-xs text-white/40 uppercase mb-2">{language === 'tr' ? 'En Popüler Sayfalar' : 'Top Pages'}</div>
                  {dashboard.visitors.topPages.map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
                      <span className="text-white/70 font-mono">{p.path}</span>
                      <span className="text-white/40">{p.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={32} className="animate-spin text-white/30" />
          </div>
        ) : (
          <>
            {/* Template Requests Tab */}
            {activeTab === 'templates' && (
              templateRequests.length === 0 ? (
                <div className="text-center py-20">
                  <FileText size={64} className="mx-auto text-white/20 mb-4" />
                  <h3 className="text-xl font-semibold text-white/60 mb-2">{t.noData}</h3>
                  <p className="text-white/40">{t.noDataDesc}</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredSubmissions.map((request, index) => (
                    <motion.div
                      key={request.request_id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Building2 size={18} className="text-[#38BDF8]" />
                            <h3 className="text-lg font-semibold text-white">
                              {request.brandName || 'Unnamed'}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
                              {getStatusText(request.status)}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-white/50">
                            <span className="flex items-center gap-1">
                              <Mail size={14} /> {request.email}
                            </span>
                            {request.phone && (
                              <span className="flex items-center gap-1">
                                <Phone size={14} /> {request.phone}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar size={14} /> {formatDate(request.createdAt)}
                            </span>
                          </div>

                          {request.colors && (
                            <div className="flex items-center gap-2 mt-3">
                              <Palette size={14} className="text-white/40" />
                              <div className="flex gap-1">
                                {['primary', 'background', 'accent'].map(key => (
                                  <div 
                                    key={key}
                                    className="w-5 h-5 rounded border border-white/20" 
                                    style={{ backgroundColor: request.colors[key] }}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Logo Display */}
                          {request.logo && request.logo.data && (
                            <div className="flex items-center gap-2 mt-3">
                              <ImageIcon size={14} className="text-white/40" />
                              <div className="flex items-center gap-2">
                                <img 
                                  src={request.logo.data} 
                                  alt="Logo" 
                                  className="w-10 h-10 rounded border border-white/20 object-contain bg-white/5"
                                />
                                <span className="text-xs text-white/40">{request.logo.filename}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <a
                            href={`mailto:${request.email}`}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#38BDF8] text-black text-sm font-medium hover:bg-white transition-colors"
                          >
                            <Mail size={14} />
                            {t.sendEmail}
                          </a>
                          <button
                            onClick={() => setSelectedItem({ type: 'template', data: request })}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 hover:text-white transition-colors text-sm"
                          >
                            <Eye size={14} />
                            {t.viewDetails}
                          </button>
                          <select
                            value={request.status || 'pending'}
                            onChange={(e) => updateStatus('template', request.id || request.request_id, e.target.value)}
                            className="px-2 py-2 rounded-lg bg-white/5 text-white/70 text-xs border border-white/10 hover:border-white/25 focus:outline-none"
                            data-testid={`status-select-template-${request.id || request.request_id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="won">Won</option>
                            <option value="lost">Lost</option>
                            <option value="completed">Completed</option>
                          </select>
                          <button
                            onClick={() => handleDeleteTemplate(request.request_id)}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors text-sm"
                          >
                            <Trash2 size={14} />
                            {t.delete}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )
            )}

            {/* Contact Submissions Tab */}
            {activeTab === 'contacts' && (
              contactSubmissions.length === 0 ? (
                <div className="text-center py-20">
                  <MessageSquare size={64} className="mx-auto text-white/20 mb-4" />
                  <h3 className="text-xl font-semibold text-white/60 mb-2">{t.noData}</h3>
                  <p className="text-white/40">{t.noDataDesc}</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredSubmissions.map((submission, index) => (
                    <motion.div
                      key={submission.submission_id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-emerald-500/20 transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <User size={18} className="text-emerald-400" />
                            <h3 className="text-lg font-semibold text-white">
                              {submission.name || 'Anonymous'}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(submission.status)}`}>
                              {getStatusText(submission.status)}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-white/50 mb-3">
                            <span className="flex items-center gap-1">
                              <Mail size={14} /> {submission.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar size={14} /> {formatDate(submission.created_at)}
                            </span>
                          </div>

                          {submission.message && (
                            <p className="text-white/60 text-sm bg-white/[0.02] p-3 rounded-lg border border-white/[0.04]">
                              &ldquo;{submission.message}&rdquo;
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <a
                            href={`mailto:${submission.email}`}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-500 text-black text-sm font-medium hover:bg-emerald-400 transition-colors"
                          >
                            <Mail size={14} />
                            {t.sendEmail}
                          </a>
                          <select
                            value={submission.status || 'new'}
                            onChange={(e) => updateStatus('contact', submission.submission_id, e.target.value)}
                            className="px-2 py-2 rounded-lg bg-white/5 text-white/70 text-xs border border-white/10 hover:border-white/25 focus:outline-none"
                            data-testid={`status-select-contact-${submission.submission_id}`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="won">Won</option>
                            <option value="lost">Lost</option>
                            <option value="completed">Completed</option>
                          </select>
                          <button
                            onClick={() => handleDeleteContact(submission.submission_id)}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors text-sm"
                          >
                            <Trash2 size={14} />
                            {t.delete}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )
            )}

            {/* Visitors Tab */}
            {activeTab === 'visitors' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                    <div className="flex items-center gap-2 text-purple-400 mb-2">
                      <Activity size={18} />
                      <span className="text-sm">{t.visitors?.today || 'Today'}</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{visitorStats?.today || 0}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-2 text-blue-400 mb-2">
                      <Users size={18} />
                      <span className="text-sm">{t.visitors?.todayUnique || 'Unique Today'}</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{visitorStats?.today_unique || 0}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2 text-emerald-400 mb-2">
                      <Globe size={18} />
                      <span className="text-sm">{t.visitors?.total || 'Total'}</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{visitorStats?.total || 0}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2 text-amber-400 mb-2">
                      <Users size={18} />
                      <span className="text-sm">{t.visitors?.totalUnique || 'Total Unique'}</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{visitorStats?.total_unique || 0}</p>
                  </div>
                </div>

                {/* Recent Visitors */}
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Clock size={18} className="text-purple-400" />
                    {t.visitors?.recent || 'Recent Visitors'}
                  </h3>
                  {visitorStats?.recent && visitorStats.recent.length > 0 ? (
                    <div className="space-y-2">
                      {visitorStats.recent.map((visitor, index) => (
                        <div 
                          key={visitor.visitor_id || index}
                          className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]"
                        >
                          <div className="flex items-center gap-4">
                            <span className="text-white/80 font-mono text-sm">{visitor.ip}</span>
                            <span className="text-purple-400 text-sm">{visitor.page}</span>
                          </div>
                          <span className="text-white/40 text-sm">
                            {new Date(visitor.timestamp).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              day: 'numeric',
                              month: 'short'
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/40 text-center py-8">{t.noData}</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Detail Modal */}
        {selectedItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setSelectedItem(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full max-w-lg bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {selectedItem.type === 'template' ? selectedItem.data.brandName : selectedItem.data.name}
                  </h2>
                  <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(selectedItem.data.status)}`}>
                    {getStatusText(selectedItem.data.status)}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                >
                  <XCircle size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs text-white/40 uppercase tracking-wider">{t.email}</label>
                  <p className="text-white mt-1">{selectedItem.data.email}</p>
                </div>

                {selectedItem.data.phone && (
                  <div>
                    <label className="text-xs text-white/40 uppercase tracking-wider">{t.phone}</label>
                    <p className="text-white mt-1">{selectedItem.data.phone}</p>
                  </div>
                )}

                {(selectedItem.data.description || selectedItem.data.message) && (
                  <div>
                    <label className="text-xs text-white/40 uppercase tracking-wider">
                      {selectedItem.type === 'template' ? t.description : t.message}
                    </label>
                    <p className="text-white/70 mt-1">{selectedItem.data.description || selectedItem.data.message}</p>
                  </div>
                )}

                {selectedItem.data.colors && (
                  <div>
                    <label className="text-xs text-white/40 uppercase tracking-wider">{t.colors}</label>
                    <div className="flex gap-3 mt-2">
                      {['primary', 'background', 'accent'].map(key => (
                        <div key={key} className="text-center">
                          <div 
                            className="w-12 h-12 rounded-lg border border-white/20 mb-1" 
                            style={{ backgroundColor: selectedItem.data.colors[key] }}
                          />
                          <span className="text-[10px] text-white/40 capitalize">{key}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Logo in detail modal */}
                {selectedItem.data.logo && selectedItem.data.logo.data && (
                  <div>
                    <label className="text-xs text-white/40 uppercase tracking-wider">Logo</label>
                    <div className="mt-2 p-4 bg-white/5 rounded-lg border border-white/10">
                      <img 
                        src={selectedItem.data.logo.data} 
                        alt="Uploaded Logo" 
                        className="max-w-full max-h-32 object-contain mx-auto"
                      />
                      <p className="text-center text-xs text-white/40 mt-2">{selectedItem.data.logo.filename}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs text-white/40 uppercase tracking-wider">{t.createdAt}</label>
                  <p className="text-white/70 mt-1">{formatDate(selectedItem.data.createdAt || selectedItem.data.created_at)}</p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/10">
                <a
                  href={`mailto:${selectedItem.data.email}`}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#38BDF8] text-black font-semibold rounded-lg hover:bg-white transition-colors"
                >
                  <Mail size={16} />
                  {t.sendEmail}
                </a>
              </div>
            </motion.div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowPasswordModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-2xl p-6"
            >
              <button
                onClick={() => setShowPasswordModal(false)}
                className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#38BDF8]/10 border border-[#38BDF8]/20 flex items-center justify-center mx-auto mb-4">
                  <Lock size={20} className="text-[#38BDF8]" />
                </div>
                <h3 className="text-lg font-semibold text-white">{t.changePassword}</h3>
              </div>

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="text-xs text-white/40 uppercase tracking-wider">{t.currentPassword}</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    className="w-full mt-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs text-white/40 uppercase tracking-wider">{t.newUsername}</label>
                  <input
                    type="text"
                    value={passwordForm.newUsername}
                    onChange={(e) => setPasswordForm({...passwordForm, newUsername: e.target.value})}
                    placeholder={localStorage.getItem('adminUsername') || 'pirone'}
                    className="w-full mt-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50"
                  />
                </div>

                <div>
                  <label className="text-xs text-white/40 uppercase tracking-wider">{t.newPassword}</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    className="w-full mt-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50"
                  />
                </div>

                <div>
                  <label className="text-xs text-white/40 uppercase tracking-wider">{t.confirmPassword}</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    className="w-full mt-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50"
                  />
                </div>

                {passwordError && (
                  <p className="text-red-400 text-sm text-center">{passwordError}</p>
                )}

                {passwordSuccess && (
                  <p className="text-green-400 text-sm text-center">{passwordSuccess}</p>
                )}

                <button
                  type="submit"
                  className="w-full py-3 bg-[#38BDF8] text-black font-semibold rounded-lg hover:bg-white transition-colors"
                >
                  {t.save}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
